// DESCRIPTION: Popover shown when the user clicks an event chip — displays event details and action buttons.

import React, { useRef, useEffect } from 'react';
import {
	formatTime,
	getTimezoneAbbr,
	getLocalTimezoneAbbr,
	getDisplayTime,
} from './utils';

const config = window.nemtlyCalendar || {};
const strings = config.strings || {};

/**
 * EventPopover
 *
 * @param {Object}   props
 * @param {Object}   props.event      Calendar event object from the API.
 * @param {boolean}  props.useLocalTz Whether to show times in browser's local timezone.
 * @param {Function} props.onClose    Called when the popover should close.
 */
export default function EventPopover( { event, useLocalTz, onClose } ) {
	const ref = useRef( null );

	// Close when clicking outside.
	useEffect( () => {
		const handleClick = ( e ) => {
			if ( ref.current && ! ref.current.contains( e.target ) ) {
				onClose();
			}
		};
		document.addEventListener( 'mousedown', handleClick );
		return () => document.removeEventListener( 'mousedown', handleClick );
	}, [ onClose ] );

	// Close on Escape.
	useEffect( () => {
		const handleKey = ( e ) => {
			if ( e.key === 'Escape' ) {
				onClose();
			}
		};
		document.addEventListener( 'keydown', handleKey );
		return () => document.removeEventListener( 'keydown', handleKey );
	}, [ onClose ] );

	const eventUrl = event.slug
		? `${ window.location.origin }/${ event.slug }/`
		: '#';

	const formatPrice = () => {
		if ( ! event.price ) {
			return null;
		}
		return new Intl.NumberFormat( undefined, {
			style: 'currency',
			currency: event.currency || 'USD',
		} ).format( event.price );
	};

	const availabilityText = () => {
		if ( event.is_unlimited ) {
			return null;
		}
		if (
			event.available_spots !== null &&
			event.available_spots !== undefined
		) {
			return `${ event.available_spots }/${ event.total_capacity } spots remaining`;
		}
		return null;
	};

	return (
		<div
			ref={ ref }
			className="nb-calendar-popover"
			role="dialog"
			aria-modal="true"
			aria-label={ event.title }
		>
			<div className="nb-calendar-popover__title">{ event.title }</div>

			<div className="nb-calendar-popover__meta">
				{ event.date && (
					<div className="nb-calendar-popover__meta-row">
						<span>📅</span>
						<span>
							{ event.date }
							{ config.showEventTime !== false && event.time
								? ` · ${ formatTime(
										getDisplayTime( event, useLocalTz )
								  ) }`
								: '' }
							{ config.showEventTime !== false && event.time
								? ` ${
										useLocalTz
											? getLocalTimezoneAbbr()
											: getTimezoneAbbr()
								  }`
								: '' }
						</span>
					</div>
				) }
				{ event.location && (
					<div className="nb-calendar-popover__meta-row">
						<span>📍</span>
						<span>{ event.location }</span>
					</div>
				) }
				{ formatPrice() && (
					<div className="nb-calendar-popover__meta-row">
						<span>💰</span>
						<span>{ formatPrice() }</span>
					</div>
				) }
				{ availabilityText() && (
					<div className="nb-calendar-popover__meta-row">
						<span>👥</span>
						<span>{ availabilityText() }</span>
					</div>
				) }
			</div>

			<div className="nb-calendar-popover__actions">
				<a href={ eventUrl } className="nb-calendar-popover__view-btn">
					{ strings.viewDetails || 'View Details' }
				</a>
				{ config.showBookingBtn !== false && event.is_bookable && (
					<a
						href={ eventUrl }
						className="nb-calendar-popover__book-btn"
					>
						{ strings.bookNow || 'Book Now' }
					</a>
				) }
			</div>
		</div>
	);
}
